import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Bestellung } from '../types';
import { downloadDateiOderFehler } from '../lib/download';
import BestellungForm from './BestellungForm';
import RowActionsMenu from './RowActionsMenu';
import './BestellungList.css';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

const BestellungList: React.FC = () => {
  const [bestellungen, setBestellungen] = useState<Bestellung[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingBestellung, setEditingBestellung] = useState<Bestellung | null>(null);
  // Inline-PDF-Viewer
  const [downloadFehler, setDownloadFehler] = useState('');

  // E-Mail Dialog
  const [emailDialogId, setEmailDialogId] = useState<number | null>(null);
  const [emailInput, setEmailInput] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailFeedback, setEmailFeedback] = useState<Record<number, string>>({});

  const loadBestellungen = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.append('status', filterStatus);
      const url = `${API_URL}/api/bestellungen${params.toString() ? '?' + params.toString() : ''}`;
      const response = await axios.get(url);
      setBestellungen(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Bestellungen:', error);
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  useEffect(() => {
    loadBestellungen();
  }, [loadBestellungen]);

  const getStatusClass = (status: string) => {
    const map: Record<string, string> = {
      'Entwurf': 'best-status-entwurf',
      'Bestellt': 'best-status-bestellt',
      'Teilgeliefert': 'best-status-teilgeliefert',
      'Abgeschlossen': 'best-status-abgeschlossen',
      'Storniert': 'best-status-storniert',
    };
    return map[status] || '';
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('de-DE');
  };

  const formatPrice = (price?: number) => {
    if (price === null || price === undefined) return '-';
    return `${price.toFixed(2)} \u20AC`;
  };

  const toggleExpand = (id: number) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const openNeu = () => {
    setEditingBestellung(null);
    setShowForm(true);
  };

  const openBearbeiten = (e: React.MouseEvent, best: Bestellung) => {
    e.stopPropagation();
    setEditingBestellung(best);
    setShowForm(true);
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingBestellung(null);
    loadBestellungen();
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingBestellung(null);
  };

  /** Bestell-PDF holen; den Dateinamen bestimmt der Server (Content-Disposition). */
  const bestellungHerunterladen = async (best: Bestellung) => {
    const id = best.bestellung_id;
    const nr = best.bestellung_nr || String(id).padStart(4, '0');
    setDownloadFehler('');
    const fehler = await downloadDateiOderFehler(
      `${API_URL}/api/dokumente/bestellungen/${id}/pdf`,
      `BE_${nr}.pdf`,
      'Bestellung',
    );
    if (fehler) setDownloadFehler(fehler);
  };

  const sendEmailBestellung = async (id: number) => {
    setEmailSending(true);
    try {
      await axios.post(`${API_URL}/api/email/bestellung/${id}`, { empfaenger: emailInput || undefined });
      setEmailFeedback(prev => ({ ...prev, [id]: 'E-Mail gesendet' }));
      setEmailDialogId(null);
      loadBestellungen();
    } catch (e: any) {
      setEmailFeedback(prev => ({ ...prev, [id]: e.response?.data?.detail || 'Fehler beim Senden' }));
    } finally {
      setEmailSending(false);
    }
  };

  const getLieferfortschritt = (bestellung: Bestellung) => {
    if (!bestellung.positionen || bestellung.positionen.length === 0) return null;
    const total = bestellung.positionen.reduce((sum, p) => sum + p.menge, 0);
    const geliefert = bestellung.positionen.reduce((sum, p) => sum + p.menge_geliefert, 0);
    if (total === 0) return null;
    return Math.round((geliefert / total) * 100);
  };

  if (showForm) {
    return (
      <BestellungForm
        editingBestellung={editingBestellung}
        onSuccess={handleFormSuccess}
        onCancel={handleFormCancel}
      />
    );
  }

  return (
    <div className="bestellung-list-container">
      <div className="list-header">
        <h2>Bestellungen (Einkauf)</h2>
        <div className="header-actions">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="filter-select"
          >
            <option value="">Alle Status</option>
            <option value="Entwurf">Entwurf</option>
            <option value="Bestellt">Bestellt</option>
            <option value="Teilgeliefert">Teilgeliefert</option>
            <option value="Abgeschlossen">Abgeschlossen</option>
            <option value="Storniert">Storniert</option>
          </select>
          <button className="k-btn k-btn--primary" onClick={openNeu}>+ Neue Bestellung</button>
        </div>
      </div>

      {downloadFehler && <div className="k-alert k-alert--danger">{downloadFehler}</div>}

      {loading ? (
        <div className="loading">L&auml;dt...</div>
      ) : bestellungen.length === 0 ? (
        <div className="empty-state">
          <p>Keine Bestellungen vorhanden</p>
          <p className="empty-hint">Bestellungen werden vom Einkaufs-Agent erstellt (z.B. via Chat: "Bestell 20 Stueck Artikel A-001 bei Lieferant X").</p>
        </div>
      ) : (
        <div className="table-container">
          <table className="bestellung-table">
            <thead>
              <tr>
                <th></th>
                <th>Best.-Nr.</th>
                <th>Lieferant</th>
                <th>Status</th>
                <th>Liefertermin</th>
                <th>Fortschritt</th>
                <th>Gesamt</th>
                <th>Erstellt</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {bestellungen.map((best) => {
                const fortschritt = getLieferfortschritt(best);
                return (
                  <React.Fragment key={best.bestellung_id}>
                    <tr
                      className={`bestellung-row ${expandedId === best.bestellung_id ? 'expanded' : ''}`}
                      onClick={() => toggleExpand(best.bestellung_id)}
                    >
                      <td className="expand-cell">
                        {best.positionen && best.positionen.length > 0
                          ? (expandedId === best.bestellung_id ? '\u25BC' : '\u25B6')
                          : ''}
                      </td>
                      <td className="bestellung-nr">{best.bestellung_nr}</td>
                      <td>{best.lieferant_name || '-'}</td>
                      <td>
                        <span className={`status-badge ${getStatusClass(best.status)}`}>
                          {best.status}
                        </span>
                      </td>
                      <td>{formatDate(best.liefertermin_soll)}</td>
                      <td>
                        {fortschritt !== null ? (
                          <div className="fortschritt-bar">
                            <div
                              className="fortschritt-fill"
                              style={{ width: `${fortschritt}%` }}
                            />
                            <span className="fortschritt-text">{fortschritt}%</span>
                          </div>
                        ) : '-'}
                      </td>
                      <td className="price-cell">{formatPrice(best.gesamtsumme)}</td>
                      <td>{formatDate(best.erstellt_am)}</td>
                      <td className="action-cell" onClick={(e) => e.stopPropagation()}>
                        {emailFeedback[best.bestellung_id] && (
                          <span className="email-sent-hint">{emailFeedback[best.bestellung_id]}</span>
                        )}
                        <button
                          className="btn-edit"
                          onClick={(e) => openBearbeiten(e, best)}
                          title="Bestellung bearbeiten"
                        >
                          Bearbeiten
                        </button>
                        <RowActionsMenu
                          actions={[
                            { label: 'Bestellung herunterladen', onClick: () => bestellungHerunterladen(best) },
                            ...(best.status === 'Entwurf'
                              ? [{
                                  label: 'Per E-Mail an Lieferant senden',
                                  onClick: () => {
                                    if (emailDialogId === best.bestellung_id) {
                                      setEmailDialogId(null);
                                    } else {
                                      setEmailDialogId(best.bestellung_id);
                                      setEmailInput('');
                                    }
                                  },
                                }]
                              : []),
                          ]}
                        />
                      </td>
                    </tr>
                    {emailDialogId === best.bestellung_id && (
                      <tr className="best-email-dialog-row" onClick={(e) => e.stopPropagation()}>
                        <td colSpan={9}>
                          <div className="best-email-inline">
                            <input
                              type="email"
                              placeholder="Empfänger-E-Mail (leer = Lieferant-E-Mail)"
                              value={emailInput}
                              onChange={e => setEmailInput(e.target.value)}
                              className="best-email-input"
                              autoFocus
                            />
                            <button
                              className="btn-email-confirm"
                              onClick={() => sendEmailBestellung(best.bestellung_id)}
                              disabled={emailSending}
                            >
                              {emailSending ? 'Sendet...' : 'Senden'}
                            </button>
                            <button
                              className="btn-email-cancel"
                              onClick={() => setEmailDialogId(null)}
                            >
                              Abbrechen
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                    {expandedId === best.bestellung_id && best.positionen && best.positionen.length > 0 && (
                      <tr className="detail-row">
                        <td colSpan={9}>
                          <div className="positionen-container">
                            <h4>Positionen</h4>
                            <table className="positionen-table">
                              <thead>
                                <tr>
                                  <th>Artikel</th>
                                  <th>Bestellt</th>
                                  <th>Geliefert</th>
                                  <th>Einzelpreis</th>
                                  <th>Gesamt</th>
                                  <th>Status</th>
                                </tr>
                              </thead>
                              <tbody>
                                {best.positionen.map((pos) => (
                                  <tr key={pos.position_id}>
                                    <td>{pos.artikel_bezeichnung || `#${pos.artikel_id}`}</td>
                                    <td>{pos.menge} {pos.artikel_einheit || ''}</td>
                                    <td>{pos.menge_geliefert} {pos.artikel_einheit || ''}</td>
                                    <td>{formatPrice(pos.preis_einzeln)}</td>
                                    <td>{formatPrice(pos.preis_gesamt)}</td>
                                    <td>
                                      <span className={`pos-status ${pos.ist_komplett ? 'pos-komplett' : 'pos-offen'}`}>
                                        {pos.ist_komplett ? 'Komplett' : 'Offen'}
                                      </span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            {best.notizen && (
                              <div className="bestellung-notizen">
                                <strong>Notizen:</strong> {best.notizen}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default BestellungList;
